import { z } from 'zod';
import {
  detectDayFirst,
  looksLikeHeader,
  normalizeText,
  parseImportDate,
  parseImportNumber,
  type ImportTarget,
} from './importer';

/**
 * Estructura de un archivo de ventas (punto 3 del documento de Alex, 23 oct
 * 2026: «que la IA entienda el Excel, que el código importe los números y
 * que al final cuadre con el total del archivo»).
 *
 * Los exports de TPV reales no son una tabla limpia que empieza en A1:
 *  - traen filas de título arriba («Informe de ventas · Octubre 2026»);
 *  - meten filas de total y subtotal en medio o al final;
 *  - a veces ponen los días en COLUMNAS y los métodos de pago en filas;
 *  - y los libros traen varias hojas.
 *
 * Este módulo hace dos cosas, las dos deterministas y con tests:
 *  1. `detectarEstructura` adivina dónde está la tabla. Si se equivoca, la
 *     IA (o el usuario) la corrige con la misma forma de datos.
 *  2. `aplicarEstructura` la convierte en la tabla limpia de siempre
 *     (cabeceras + una fila por registro) que ya entiende `buildRevenueImport`.
 *
 * La IA NUNCA toca los números: como mucho dice qué fila es la cabecera, qué
 * filas son totales y hacia dónde va la tabla. Los importes los lee el código.
 *
 * Índices: todo es 0-based sobre la hoja CRUDA (con las filas vacías), así
 * `fila + 1` es el número de fila que el usuario ve en Excel.
 */

export type Orientacion = 'filas' | 'columnas';

export interface EstructuraArchivo {
  orientacion: Orientacion;
  /** Fila de cabecera (en `columnas`: la fila con las fechas). null = sin cabecera. */
  filaCabecera: number | null;
  /** Primera y última fila con datos (inclusive). */
  primeraFila: number;
  ultimaFila: number;
  /** Filas de total o subtotal dentro de la tabla: no se importan. */
  excluir: number[];
  /** Fila del total general, para cuadrar (solo `filas`). */
  filaTotal: number | null;
  /** Solo `columnas`: columna con los nombres de cada fila (Efectivo, Tarjeta…). */
  columnaEtiquetas?: number;
}

export interface ArchivoEstructurado {
  cabeceras: string[];
  tieneCabecera: boolean;
  filas: string[][];
  /** Dónde estaba cada fila en el archivo, para los mensajes: «fila 12», «columna D». */
  origen: string[];
  /** El total del archivo, con la misma forma que `filas`. null si no trae. */
  total: string[] | null;
  /** Filas que no se importan y por qué (totales y subtotales). */
  descartadas: Array<{ origen: string; motivo: string }>;
  /** Solo `columnas`: fila de la hoja de la que sale cada cabecera a partir de la 1 (la 0 es la fecha). */
  filasEtiqueta?: number[];
}

/** Letra de columna al estilo de Excel: 0→A, 25→Z, 26→AA… */
export function letraColumna(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

const vacia = (fila: string[] | undefined): boolean => !fila || fila.every((c) => (c ?? '').trim() === '');
const esFecha = (celda: string | undefined): boolean => parseImportDate(celda, true) !== undefined;

/** Hasta dónde se busca la cabecera: un informe no tiene 60 filas de título. */
const FILAS_DE_BUSQUEDA = 60;
/** Una fila con tantas fechas es la cabecera de una tabla con los días en columnas. */
const FECHAS_PARA_COLUMNAS = 3;

const TOTAL_RE = /^(sub ?)?total(es)?\b|^suma\b|^acumulado\b|^gran total\b/;

/**
 * ¿Es una etiqueta de total? «Total», «TOTAL GENERAL», «Subtotal sala»,
 * «Suma», «Acumulado». Un número nunca lo es.
 */
export function esEtiquetaDeTotal(celda: string | undefined): boolean {
  const crudo = (celda ?? '').trim();
  if (!crudo || crudo.length > 40 || parseImportNumber(crudo) !== undefined || esFecha(crudo)) return false;
  return TOTAL_RE.test(normalizeText(crudo));
}

const esSubtotal = (celda: string): boolean => /^sub ?total/.test(normalizeText(celda));

/**
 * La etiqueta de total de una fila, si la tiene. Solo se mira la parte de
 * «concepto», a la izquierda del primer importe: una columna de observaciones
 * al final con «Total con propinas» no convierte un día en un total.
 */
function etiquetaDeTotal(fila: string[]): string | undefined {
  for (const c of fila) {
    if (esEtiquetaDeTotal(c)) return c;
    if (parseImportNumber(c) !== undefined) return undefined;
  }
  return undefined;
}

/**
 * Con varias hojas, la que tiene pinta de ventas: la que más fechas trae.
 * A igualdad, la primera (es la que se cogía antes).
 */
export function elegirHoja(hojas: Array<{ nombre: string; filas: string[][] }>): number {
  let mejor = 0;
  let puntos = -1;
  hojas.forEach((hoja, i) => {
    let fechas = 0;
    for (const fila of hoja.filas.slice(0, 400)) for (const c of fila) if (esFecha(c)) fechas++;
    if (fechas > puntos) {
      puntos = fechas;
      mejor = i;
    }
  });
  return mejor;
}

/** Pista que corrige la detección: la de la IA o la de un formato recordado. */
export interface PistaEstructura {
  orientacion?: Orientacion;
  filaCabecera?: number | null;
  /** Filas que además hay que saltar (las que la IA ha visto que son totales). */
  excluir?: number[];
  /** Fila del total general, si la IA la ha visto. */
  filaTotal?: number | null;
}

/** Adivina dónde está la tabla. Siempre devuelve algo utilizable. */
export function detectarEstructura(raw: string[][], pista: PistaEstructura = {}): EstructuraArchivo {
  const est = detectarSinPistaDeTotales(raw, pista);
  /*
   * Lo que la IA ha marcado se SUMA a lo detectado, pero solo si la fila tiene
   * pinta de total (revisión de seguridad, 27 oct 2026): un texto metido en una
   * celda del export no puede convencer al modelo de saltarse días de verdad.
   *  - En `filas`: se acepta si la fila trae una etiqueta de total o no trae
   *    ninguna fecha (un día real siempre la trae).
   *  - En `columnas`: solo si la etiqueta de la fila es de total.
   * Y la IA no puede QUITAR el total detectado: como mucho elegir otra fila
   * que también sea de total. Sin total no hay cuadre, y el cuadre es la red.
   */
  const dentro = (r: number) => Number.isInteger(r) && r >= est.primeraFila && r <= est.ultimaFila;
  const conFecha = columnaDeFechas(raw)?.columna;
  const pintaDeTotal = (r: number): boolean => {
    const fila = raw[r] ?? [];
    if (est.orientacion === 'columnas') return esEtiquetaDeTotal(fila[est.columnaEtiquetas ?? 0]);
    if (etiquetaDeTotal(fila)) return true;
    return conFecha === undefined ? false : !esFecha(fila[conFecha]) && !fila.some(esFecha);
  };
  const excluir = new Set(est.excluir);
  for (const r of pista.excluir ?? []) if (dentro(r) && pintaDeTotal(r)) excluir.add(r);
  let filaTotal = est.filaTotal;
  if (
    pista.filaTotal != null &&
    est.orientacion === 'filas' &&
    dentro(pista.filaTotal) &&
    etiquetaDeTotal(raw[pista.filaTotal] ?? []) !== undefined &&
    (conFecha === undefined || !esFecha(raw[pista.filaTotal]?.[conFecha]))
  ) {
    filaTotal = pista.filaTotal;
    excluir.add(filaTotal);
  }
  return { ...est, excluir: [...excluir].sort((a, b) => a - b), filaTotal };
}

/** La columna con más fechas (la de los días en orientación `filas`) y cuántas trae. */
function columnaDeFechas(raw: string[][]): { columna: number; cuenta: number } | undefined {
  const cuenta = new Map<number, number>();
  for (const fila of raw) fila.forEach((c, i) => esFecha(c) && cuenta.set(i, (cuenta.get(i) ?? 0) + 1));
  const mejor = [...cuenta.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  return mejor ? { columna: mejor[0], cuenta: mejor[1] } : undefined;
}

/** La racha más larga de fechas seguidas en una fila. */
function rachaDeFechas(fila: string[]): number {
  let mejor = 0;
  let racha = 0;
  for (const c of fila) {
    racha = esFecha(c) ? racha + 1 : 0;
    mejor = Math.max(mejor, racha);
  }
  return mejor;
}

function detectarSinPistaDeTotales(raw: string[][], pista: PistaEstructura): EstructuraArchivo {
  const n = raw.length;
  let ultima = n - 1;
  while (ultima > 0 && vacia(raw[ultima])) ultima--;
  const tope = Math.min(n, FILAS_DE_BUSQUEDA);

  /*
   * Días en columnas: una fila con fechas SEGUIDAS, y más de las que trae
   * cualquier columna. Un cierre de caja con «Fecha | Apertura | Cierre» tiene
   * tres fechas por fila, pero muchas más en cada columna: va por filas.
   */
  const porColumna = columnaDeFechas(raw)?.cuenta ?? 0;
  const filaConFechasEnColumnas = (): number => {
    for (let r = 0; r < tope; r++) {
      const racha = rachaDeFechas(raw[r] ?? []);
      if (racha >= FECHAS_PARA_COLUMNAS && racha > porColumna) return r;
    }
    return -1;
  };

  const orientacion: Orientacion =
    pista.orientacion ?? (filaConFechasEnColumnas() >= 0 ? 'columnas' : 'filas');

  if (orientacion === 'columnas') {
    const cabecera =
      pista.filaCabecera != null && pista.filaCabecera >= 0 && pista.filaCabecera < n
        ? pista.filaCabecera
        : Math.max(0, filaConFechasEnColumnas());
    const fechas = raw[cabecera] ?? [];
    const primeraFecha = fechas.findIndex(esFecha);
    // Las etiquetas van a la izquierda de la primera fecha: la última columna
    // con texto antes de ella (suele ser la A).
    let etiquetas = 0;
    for (let c = Math.max(0, primeraFecha - 1); c >= 0; c--) {
      if (raw.slice(cabecera + 1, ultima + 1).some((f) => (f[c] ?? '').trim() !== '' && parseImportNumber(f[c]) === undefined)) {
        etiquetas = c;
        break;
      }
    }
    return {
      orientacion,
      filaCabecera: cabecera,
      primeraFila: Math.min(cabecera + 1, ultima),
      ultimaFila: ultima,
      excluir: [],
      filaTotal: null,
      columnaEtiquetas: etiquetas,
    };
  }

  // Filas: la columna de fechas es la que más fechas tiene.
  const columnaFecha = columnaDeFechas(raw)?.columna;
  const conFechaEn = (r: number): boolean => columnaFecha !== undefined && esFecha(raw[r]?.[columnaFecha]);
  /*
   * Primera fila de datos: la primera con fecha a la que sigue otra con fecha.
   * Una fecha suelta en el título («Generado el 27/10/2026») no es un día.
   */
  const fechadas: number[] = [];
  for (let r = 0; r < n; r++) if (conFechaEn(r)) fechadas.push(r);
  const siguienteLlena = (r: number): number => {
    for (let k = r + 1; k < n; k++) if (!vacia(raw[k])) return k;
    return -1;
  };
  const primeraConFecha =
    fechadas.find((r) => {
      const sig = siguienteLlena(r);
      return sig >= 0 && conFechaEn(sig);
    }) ??
    fechadas[0] ??
    -1;
  const ultimaConFecha = fechadas.length > 0 ? fechadas[fechadas.length - 1]! : -1;

  let cabecera: number | null = null;
  if (pista.filaCabecera !== undefined) {
    cabecera = pista.filaCabecera !== null && pista.filaCabecera >= 0 && pista.filaCabecera < n ? pista.filaCabecera : null;
  } else if (primeraConFecha > 0) {
    // La cabecera es la fila con texto justo encima de los datos (hasta 10 arriba).
    for (let r = primeraConFecha - 1; r >= Math.max(0, primeraConFecha - 10); r--) {
      const fila = raw[r] ?? [];
      if (vacia(fila)) continue;
      const llenas = fila.filter((c) => (c ?? '').trim() !== '').length;
      if (llenas >= 2 && looksLikeHeader(fila)) cabecera = r;
      break;
    }
  } else if (primeraConFecha === -1 && n > 0) {
    // Sin fechas (un ranking de platos): la primera fila con al menos dos
    // celdas que parezca cabecera; un título de una sola celda no lo es.
    const r = raw.slice(0, tope).findIndex(
      (f) => f.filter((c) => (c ?? '').trim() !== '').length >= 2 && looksLikeHeader(f),
    );
    const primeraLlena = raw.findIndex((f) => !vacia(f));
    cabecera = r >= 0 ? r : primeraLlena >= 0 && looksLikeHeader(raw[primeraLlena]!) ? primeraLlena : null;
  }

  const primera =
    cabecera !== null ? cabecera + 1 : primeraConFecha >= 0 ? primeraConFecha : Math.max(0, raw.findIndex((f) => !vacia(f)));

  const excluir: number[] = [];
  let filaTotal: number | null = null;
  for (let r = primera; r <= ultima; r++) {
    const fila = raw[r];
    if (vacia(fila)) continue;
    const etiqueta = etiquetaDeTotal(fila!);
    if (!etiqueta) continue;
    excluir.push(r);
    /*
     * El total general: el primer «Total» sin fecha DESPUÉS del último día.
     * Ni el total de un turno (lleva fecha) ni el «Total IVA» del pie.
     */
    if (filaTotal === null && !esSubtotal(etiqueta) && r > ultimaConFecha && !conFechaEn(r)) filaTotal = r;
  }

  return {
    orientacion,
    filaCabecera: cabecera,
    primeraFila: primera,
    ultimaFila: Math.max(primera, ultima),
    excluir,
    filaTotal,
  };
}

/**
 * La tabla limpia: cabeceras y una fila por registro, sin títulos ni totales.
 *
 * `saltarTotales` = false vuelve a meter los subtotales (por si el detector se
 * ha pasado de listo); el total general no entra nunca: no es un día.
 */
export function aplicarEstructura(
  raw: string[][],
  est: EstructuraArchivo,
  saltarTotales = true,
): ArchivoEstructurado {
  const excluidas = new Set(est.excluir);
  const descartadas: ArchivoEstructurado['descartadas'] = [];
  const ultima = Math.min(est.ultimaFila, raw.length - 1);

  if (est.orientacion === 'columnas') {
    const cab = est.filaCabecera ?? 0;
    const L = est.columnaEtiquetas ?? 0;
    const cabecera = raw[cab] ?? [];
    const filasEtiqueta: number[] = [];
    for (let r = est.primeraFila; r <= ultima; r++) {
      const fila = raw[r];
      if (vacia(fila) || (fila![L] ?? '').trim() === '') continue;
      if (saltarTotales && excluidas.has(r)) {
        descartadas.push({ origen: `fila ${r + 1}`, motivo: 'marcada como total' });
        continue;
      }
      filasEtiqueta.push(r);
    }
    const cabeceras = ['Fecha', ...filasEtiqueta.map((r) => (raw[r]![L] ?? '').trim())];
    const filas: string[][] = [];
    const origen: string[] = [];
    let total: string[] | null = null;
    const ancho = filasEtiqueta.reduce((m, r) => Math.max(m, raw[r]!.length), cabecera.length);
    for (let c = L + 1; c < ancho; c++) {
      const h = (cabecera[c] ?? '').trim();
      const valores = filasEtiqueta.map((r) => (raw[r]![c] ?? '').trim());
      if (!h && valores.every((v) => v === '')) continue;
      if (esEtiquetaDeTotal(h)) {
        if (!esSubtotal(h) && total === null) {
          total = [h, ...valores];
          descartadas.push({ origen: `columna ${letraColumna(c)}`, motivo: 'total del archivo (para cuadrar)' });
        } else {
          descartadas.push({ origen: `columna ${letraColumna(c)}`, motivo: 'subtotal' });
        }
        continue;
      }
      filas.push([h, ...valores]);
      origen.push(`columna ${letraColumna(c)}`);
    }
    return { cabeceras, tieneCabecera: true, filas, origen, total, descartadas, filasEtiqueta };
  }

  const cabecera = est.filaCabecera !== null ? raw[est.filaCabecera] ?? [] : null;
  const rango = raw.slice(est.primeraFila, ultima + 1);
  const ancho = rango.reduce((m, f) => Math.max(m, f.length), cabecera?.length ?? 0);
  const pad = (fila: string[]): string[] => Array.from({ length: ancho }, (_, i) => (fila[i] ?? '').trim());

  const filas: string[][] = [];
  const origen: string[] = [];
  let total: string[] | null = null;
  for (let r = est.primeraFila; r <= ultima; r++) {
    const fila = raw[r];
    if (vacia(fila)) continue;
    if (r === est.filaTotal) {
      total = pad(fila!);
      descartadas.push({ origen: `fila ${r + 1}`, motivo: 'total del archivo (para cuadrar)' });
      continue;
    }
    if (saltarTotales && excluidas.has(r)) {
      descartadas.push({ origen: `fila ${r + 1}`, motivo: 'fila de total o subtotal' });
      continue;
    }
    filas.push(pad(fila!));
    origen.push(`fila ${r + 1}`);
  }
  return {
    cabeceras: cabecera ? pad(cabecera) : Array.from({ length: ancho }, () => ''),
    tieneCabecera: cabecera !== null,
    filas,
    origen,
    total,
    descartadas,
  };
}

/* ------------------------------------------------------------------ */
/* Cuadre con el total del archivo                                     */
/* ------------------------------------------------------------------ */

export interface CuadreColumna {
  indice: number;
  cabecera: string;
  /** Lo que suman las filas que se van a importar (tal cual, sin quitar IVA). */
  leido: number;
  /** Lo que dice la fila de total del archivo. */
  archivo: number;
  diferencia: number;
  cuadra: boolean;
}

export interface CuadreConTotal {
  /** `doble_conteo`: hay una columna de total del día Y desglose: se sumarían dos veces. */
  estado: 'sin_total' | 'sin_columnas' | 'cuadra' | 'no_cuadra' | 'doble_conteo';
  columnas: CuadreColumna[];
}

const DINERO = new Set<ImportTarget['kind']>(['total', 'inHouse', 'delivery']);
const r2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * ¿Lo que vamos a leer suma lo mismo que el total del propio archivo?
 *
 * Columna a columna de dinero, y con los números TAL CUAL vienen (antes de
 * quitar IVA): se comprueba que hemos leído bien el archivo, no el análisis.
 * Solo cuentan las filas con fecha, que son las que se importan.
 */
export function cuadrarConTotal(archivo: ArchivoEstructurado, targets: ImportTarget[]): CuadreConTotal {
  // «Ventas totales del día» + efectivo/tarjeta/plataformas: cada columna
  // cuadraría con su total, pero el día se contaría dos veces.
  if (targets.some((t) => t.kind === 'total') && targets.some((t) => t.kind === 'inHouse' || t.kind === 'delivery')) {
    return { estado: 'doble_conteo', columnas: [] };
  }
  if (!archivo.total) return { estado: 'sin_total', columnas: [] };
  const fecha = targets.findIndex((t) => t.kind === 'date');
  if (fecha === -1) return { estado: 'sin_columnas', columnas: [] };
  const dayFirst = detectDayFirst(archivo.filas.map((f) => f[fecha]));
  const conFecha = archivo.filas.filter((f) => parseImportDate(f[fecha], dayFirst) !== undefined);

  const columnas: CuadreColumna[] = [];
  targets.forEach((t, i) => {
    if (!DINERO.has(t.kind)) return;
    const enTotal = parseImportNumber(archivo.total![i]);
    if (enTotal === undefined) return;
    const leido = r2(conFecha.reduce((s, f) => s + (parseImportNumber(f[i]) ?? 0), 0));
    const diferencia = r2(leido - enTotal);
    // Céntimos de redondeo por fila, con un tope relativo pequeño.
    const tolerancia = Math.max(0.05, Math.min(Math.abs(enTotal) * 0.0001, 0.01 * conFecha.length + 0.05));
    columnas.push({
      indice: i,
      cabecera: archivo.cabeceras[i] || letraColumna(i),
      leido,
      archivo: r2(enTotal),
      diferencia,
      cuadra: Math.abs(diferencia) <= tolerancia,
    });
  });
  if (columnas.length === 0) return { estado: 'sin_columnas', columnas };
  return { estado: columnas.every((c) => c.cuadra) ? 'cuadra' : 'no_cuadra', columnas };
}

/* ------------------------------------------------------------------ */
/* Lo que se recuerda de un formato y lo que propone la IA              */
/* ------------------------------------------------------------------ */

/** Lo que se guarda con el mapeo: lo justo para volver a encontrar la tabla. */
export interface EstructuraRecordada {
  hoja?: string;
  orientacion: Orientacion;
  filaCabecera: number | null;
}

export const estructuraRecordadaSchema = z.object({
  hoja: z.string().max(120).optional(),
  orientacion: z.enum(['filas', 'columnas']),
  filaCabecera: z.number().int().min(0).max(5000).nullable(),
});
